import { OnboardMode } from "@constants/misc";
import { Paths } from "@constants/paths";
import useAuthSession from "@hooks/useAuthSession";
import { _MemberSettingOnboardDialogLayout, AuthStatus } from "@types";
import { useEffect, useRef } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { openOnboardingDialog } from "../../utils/trigger";

type OnboardingTypeLayout = "drawer" | "full";

// Mapping params value to layout enum
// Easy to customize params value based on client requirement
const PARAMS_LAYOUT_MAP: Record<OnboardingTypeLayout, _MemberSettingOnboardDialogLayout> = {
  drawer: _MemberSettingOnboardDialogLayout.DIALOG_DRAWER,
  full: _MemberSettingOnboardDialogLayout.DIALOG_PAGE,
};

const TRIGGER_PATH_MODE_MAP: Record<string, OnboardMode> = {
  [Paths.PUBLIC.ONBOARD_LOGIN_DIALOG]: OnboardMode.LOGIN,
  [Paths.PUBLIC.ONBOARD_REGISTER_DIALOG]: OnboardMode.REGISTER,
  [Paths.PUBLIC.ONBOARD_DIALOG]: OnboardMode.REGISTER,
  [Paths.PUBLIC.REFERRAL_CALLBACK]: OnboardMode.REGISTER,
};

const TRIGGER_PATHS = Object.keys(TRIGGER_PATH_MODE_MAP);

/**
 * Trigger onboarding dialog from url params or pathname
 * Params:
 * - o: {@link OnboardMode}
 * - ot: {@link OnboardingTypeLayout} - optional
 *
 * Paths:
 * Paths.PUBLIC.ONBOARD_LOGIN_DIALOG => OnboardMode.LOGIN
 * Paths.PUBLIC.ONBOARD_REGISTER_DIALOG => OnboardMode.REGISTER
 * Paths.PUBLIC.ONBOARD_DIALOG => OnboardMode.REGISTER
 * Paths.Paths.PUBLIC.REFERRAL_CALLBACK => OnboardMode.REGISTER
 *
 * Any link with 'o' param will trigger onboarding dialog
 * If 'ot' param is provided, it will use the layout specified in {@link PARAMS_LAYOUT_MAP}
 * Else, it will use the default layout specified in 'useMemberSettings.onboardDialogLayout'
 */
const DialogUrlTrigger = () => {
  const { status } = useAuthSession();
  const { search, pathname } = useLocation();
  const searchParams = new URLSearchParams(search);
  const mode = searchParams.get("o");
  const layout = searchParams.get("ot");
  const openDialogEventQueue = useRef<{ mode: OnboardMode; layout?: _MemberSettingOnboardDialogLayout }[]>([]);

  const openDialog = (mode: OnboardMode, layout?: _MemberSettingOnboardDialogLayout) => {
    openDialogEventQueue.current.push({ mode, layout });
  };

  useEffect(() => {
    // ignore onboarding dialog if user is authenticated
    if (status === AuthStatus.Authenticated) return;

    // get layout from params if provided
    let onboardingLayout = undefined;
    if (layout && validateLayout(layout)) {
      onboardingLayout = PARAMS_LAYOUT_MAP[layout];
    }

    // trigger if pathname is in TRIGGER_PATHS, higher priority than 'o'
    const triggerPath = TRIGGER_PATHS.find((path) => matchPath(path, pathname));
    if (triggerPath) {
      openDialog(TRIGGER_PATH_MODE_MAP[triggerPath], onboardingLayout);
      return;
    }

    // trigger if mode is provided
    if (mode && validateMode(mode)) {
      openDialog(mode, onboardingLayout);
    }
  }, [mode, layout, status, pathname]);

  // release event queue once authentication completed
  useEffect(() => {
    if (status !== AuthStatus.Authenticating) {
      if (status === AuthStatus.Authenticated || status === AuthStatus.PasswordExpired) {
        // ignore onboarding dialog if user is authenticated
        // clear event queue
        openDialogEventQueue.current = [];
        return;
      }

      if (status === AuthStatus.Unauthenticated) {
        const lastEvent = openDialogEventQueue.current.pop();
        if (lastEvent) {
          openOnboardingDialog(lastEvent);
          // clear event queue
          openDialogEventQueue.current = [];
        }
      }
    }
  }, [status]);

  return null;
};

const validateMode = (mode: string): mode is OnboardMode => {
  const isModeValid = [OnboardMode.LOGIN, OnboardMode.REGISTER].includes(mode as OnboardMode);
  return isModeValid;
};

const validateLayout = (layout: string): layout is OnboardingTypeLayout => {
  return Object.keys(PARAMS_LAYOUT_MAP).includes(layout);
};

export default DialogUrlTrigger;
